/**
 * DOS518 - Digital Operations System for Wildfire Management
 * Copyright (c) 2024-2025 Angelo Irtilaç — All rights reserved.
 *
 * ModaleInvioEmail.tsx
 * Modale per selezione destinatario e invio scheda COAU via email.
 * Apre KMail/client email con destinatario, oggetto e corpo precompilati.
 */

import React, { useState, useMemo, useEffect } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { Capacitor } from '@capacitor/core';
import { EmailComposer } from 'capacitor-email-composer';
import {
  ContattoRubrica,
  getRubricaCompleta,
  loadContattiCustom,
  saveContattiCustom,
  RUBRICA_DEFAULT,
  CONTATTI_SPECIALI,
} from '../../data/rubricaContatti';

interface ModalInvioEmailProps {
  onClose: () => void;
  // Dati per precompilare oggetto e corpo email
  comune?: string;
  provincia?: string;
  dataOra?: string;
  nominativoDOS?: string;
  riepilogoScheda?: string;
  nomeAllegato?: string;
}

type Vista = 'rubrica' | 'aggiungi' | 'modifica';

const ModalInvioEmail: React.FC<ModalInvioEmailProps> = ({
  onClose,
  comune = '',
  provincia = '',
  dataOra,
  nominativoDOS = '',
  riepilogoScheda = '',
  nomeAllegato = '',
}) => {
  const [vista, setVista] = useState<Vista>('rubrica');
  const [ricerca, setRicerca] = useState('');
  const [regioneFiltro, setRegioneFiltro] = useState('');
  const [selezionati, setSelezionati] = useState<string[]>([]); // id contatti selezionati
  const [contattiCustom, setContattiCustom] = useState<ContattoRubrica[]>(loadContattiCustom);
  const [contattoEdit, setContattoEdit] = useState<Partial<ContattoRubrica>>({});
  const [erroreForm, setErroreForm] = useState('');

  const rubricaCompleta = useMemo(() => [
    ...CONTATTI_SPECIALI,
    ...contattiCustom,
    ...RUBRICA_DEFAULT,
  ], [contattiCustom]);

  const regioni = useMemo(() =>
    [...new Set(rubricaCompleta.map(c => c.regione))].sort(),
    [rubricaCompleta]
  );

  const contattiFiltrati = useMemo(() => {
    let lista = rubricaCompleta;
    if (regioneFiltro) lista = lista.filter(c => c.regione === regioneFiltro);
    if (ricerca.trim()) {
      const q = ricerca.toLowerCase();
      lista = lista.filter(c =>
        c.nome.toLowerCase().includes(q) ||
        c.email.toLowerCase().includes(q) ||
        c.provincia.toLowerCase().includes(q) ||
        c.sigla.toLowerCase().includes(q)
      );
    }
    return lista;
  }, [rubricaCompleta, regioneFiltro, ricerca]);

  const toggleSelezione = (id: string) => {
    setSelezionati(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  const contattiSelezionati = rubricaCompleta.filter(c => selezionati.includes(c.id));

  // Costruisce e apre il composer email
  const inviaEmail = async () => {
    if (selezionati.length === 0) return;

    const ora = dataOra || new Date().toLocaleString('it-IT', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });

    const toList = contattiSelezionati.map(c => c.email);
    const subject = `SCHEDA COAU — ${[comune, provincia].filter(Boolean).join(' ') || 'Intervento AIB'} — ${ora}`;
    const body = [
      `Buongiorno,`,
      ``,
      `si trasmette la Scheda COAU relativa all'intervento AIB in oggetto.`,
      ``,
      `📍 Località: ${[comune, provincia].filter(Boolean).join(' — ') || 'N.D.'}`,
      `🕐 Data/Ora: ${ora}`,
      nominativoDOS ? `👤 DOS: ${nominativoDOS}` : '',
      ``,
      riepilogoScheda ? `RIEPILOGO SCHEDA:\n${riepilogoScheda}\n` : '',
      nomeAllegato ? `Il file della scheda è allegato alla presente.` : `La scheda completa è riportata nel corpo del messaggio.`,
      ``,
      `Distinti saluti.`,
      ``,
      nominativoDOS || 'DOS',
      `— Sistema DOS518 IRTILAC`,
    ].filter(r => r !== undefined).join('\n');

    if (Capacitor.isNativePlatform()) {
      await EmailComposer.open({ to: toList, subject, body });
      onClose();
    } else {
      const mailto = `mailto:${encodeURIComponent(toList.join(','))}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
      window.open(mailto, '_system');
      setTimeout(() => onClose(), 300);
    }
  };

  // Salva contatto custom
  const salvaContatto = () => {
    if (!contattoEdit.nome?.trim()) { setErroreForm('Nome obbligatorio'); return; }
    if (!contattoEdit.email?.trim()) { setErroreForm('Email obbligatoria'); return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contattoEdit.email)) { setErroreForm('Email non valida'); return; }

    const nuovi = contattoEdit.id
      ? contattiCustom.map(c => c.id === contattoEdit.id ? { ...c, ...contattoEdit } as ContattoRubrica : c)
      : [...contattiCustom, {
          id: uuidv4(),
          nome: contattoEdit.nome,
          email: contattoEdit.email,
          regione: contattoEdit.regione || 'Altro',
          provincia: contattoEdit.provincia || '',
          sigla: contattoEdit.sigla || '',
          note: contattoEdit.note || '',
          custom: true,
        } as ContattoRubrica];

    setContattiCustom(nuovi);
    saveContattiCustom(nuovi);
    setVista('rubrica');
    setContattoEdit({});
    setErroreForm('');
  };

  const eliminaContatto = (id: string) => {
    const nuovi = contattiCustom.filter(c => c.id !== id);
    setContattiCustom(nuovi);
    saveContattiCustom(nuovi);
    setSelezionati(prev => prev.filter(x => x !== id));
  };

  const apriModifica = (c: ContattoRubrica) => {
    setContattoEdit({ ...c });
    setVista('modifica');
  };

  return (
    <div style={S.overlay}>
      <div style={S.modal}>

        {/* HEADER */}
        <div style={S.header}>
          <div>
            <div style={S.headerTitle}>📧 INVIA SCHEDA COAU</div>
            <div style={S.headerSub}>Seleziona uno o più destinatari dalla rubrica</div>
          </div>
          <button onClick={onClose} style={S.closeBtn}>✕</button>
        </div>

        {/* ── VISTA RUBRICA ── */}
        {vista === 'rubrica' && (
          <>
            {/* Filtri */}
            <div style={S.filtriBar}>
              <input
                style={S.searchInput}
                placeholder="🔍 Cerca per nome, provincia, email…"
                value={ricerca}
                onChange={e => setRicerca(e.target.value)}
              />
              <select
                style={S.selectRegione}
                value={regioneFiltro}
                onChange={e => setRegioneFiltro(e.target.value)}
              >
                <option value="">Tutte le regioni</option>
                {regioni.map(r => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>

            {/* Avviso allegato */}
            {nomeAllegato && (
              <div style={S.allegatoNotice}>
                <span style={{ fontSize: 16 }}>📎</span>
                <div>
                  <div style={{ fontWeight: 700, color: '#fbbf24' }}>Allegato salvato: {nomeAllegato}</div>
                  <div style={{ fontSize: 11, color: '#92400e', marginTop: 2 }}>
                    Quando si apre il client email, allega manualmente questo file prima di inviare.
                  </div>
                </div>
              </div>
            )}

            {/* Lista contatti */}
            <div style={S.lista}>
              {contattiFiltrati.length === 0 && (
                <div style={S.vuoto}>Nessun contatto trovato</div>
              )}
              {contattiFiltrati.map(c => {
                const isSelected = selezionati.includes(c.id);
                const isCustom = c.custom;
                const isSpeciale = CONTATTI_SPECIALI.some(s => s.id === c.id);
                return (
                  <div
                    key={c.id}
                    style={{
                      ...S.riga,
                      background: isSelected ? '#0f2a3a' : '#111827',
                      border: `1px solid ${isSelected ? '#0ea5e9' : '#1e3a5f'}`,
                    }}
                    onClick={() => toggleSelezione(c.id)}
                  >
                    <div style={S.rigaCheck}>
                      <div style={{
                        ...S.checkbox,
                        background: isSelected ? '#0ea5e9' : 'transparent',
                        borderColor: isSelected ? '#0ea5e9' : '#334155',
                      }}>
                        {isSelected && <span style={{ color: '#fff', fontSize: 11, fontWeight: 700 }}>✓</span>}
                      </div>
                    </div>
                    <div style={S.rigaInfo}>
                      <div style={S.rigaNome}>
                        {isSpeciale && <span style={S.badgeSpeciale}>★</span>}
                        {isCustom && <span style={S.badgeCustom}>✎</span>}
                        {c.nome}
                      </div>
                      <div style={S.rigaEmail}>{c.email}</div>
                      {c.note && <div style={S.rigaNote}>{c.note}</div>}
                    </div>
                    <div style={S.rigaSigla}>{c.sigla}</div>
                    {(isCustom || isSpeciale) && (
                      <div style={S.rigaAzioni} onClick={e => e.stopPropagation()}>
                        <button style={S.btnEdit} onClick={() => apriModifica(c)} title="Modifica">✎</button>
                        {isCustom && (
                          <button style={S.btnDel} onClick={() => eliminaContatto(c.id)} title="Elimina">✕</button>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Footer rubrica */}
            <div style={S.footer}>
              <button
                style={S.btnAggiungi}
                onClick={() => { setContattoEdit({}); setVista('aggiungi'); }}
              >
                ＋ Aggiungi contatto
              </button>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                {selezionati.length > 0 && (
                  <span style={S.selezionatiLabel}>
                    {selezionati.length} selezionat{selezionati.length === 1 ? 'o' : 'i'}
                  </span>
                )}
                <button onClick={onClose} style={S.btnAnnulla}>Annulla</button>
                <button
                  onClick={inviaEmail}
                  disabled={selezionati.length === 0}
                  style={{
                    ...S.btnInvia,
                    opacity: selezionati.length > 0 ? 1 : 0.4,
                    cursor: selezionati.length > 0 ? 'pointer' : 'not-allowed',
                  }}
                >
                  📧 Apri email
                </button>
              </div>
            </div>
          </>
        )}

        {/* ── VISTA AGGIUNGI / MODIFICA ── */}
        {(vista === 'aggiungi' || vista === 'modifica') && (
          <>
            <div style={S.formBody}>
              <div style={S.formTitle}>
                {vista === 'aggiungi' ? '＋ Nuovo contatto' : '✎ Modifica contatto'}
              </div>

              {[
                { label: 'Nome *', field: 'nome', placeholder: 'es. COP Catania - Corpo Forestale' },
                { label: 'Email *', field: 'email', placeholder: 'es. protezione.civile@pec.provincia.catania.it' },
                { label: 'Regione', field: 'regione', placeholder: 'es. Sicilia' },
                { label: 'Provincia', field: 'provincia', placeholder: 'es. Catania' },
                { label: 'Sigla', field: 'sigla', placeholder: 'es. CT' },
                { label: 'Note', field: 'note', placeholder: 'es. Contattare solo in orario ufficio' },
              ].map(({ label, field, placeholder }) => (
                <div key={field} style={S.formGrp}>
                  <label style={S.formLabel}>{label}</label>
                  <input
                    style={S.formInput}
                    value={(contattoEdit as any)[field] || ''}
                    onChange={e => setContattoEdit(prev => ({ ...prev, [field]: e.target.value }))}
                    placeholder={placeholder}
                  />
                </div>
              ))}

              {erroreForm && <div style={S.erroreForm}>{erroreForm}</div>}
            </div>

            <div style={S.footer}>
              <button onClick={() => { setVista('rubrica'); setErroreForm(''); }} style={S.btnAnnulla}>
                ← Indietro
              </button>
              <button onClick={salvaContatto} style={S.btnInvia}>
                💾 Salva
              </button>
            </div>
          </>
        )}

      </div>
    </div>
  );
};

// ─── Stili ────────────────────────────────────────────────────

const S: Record<string, React.CSSProperties> = {
  overlay: {
    position: 'fixed', inset: 0,
    background: 'rgba(0,0,0,0.85)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 9999, padding: 16,
  },
  modal: {
    background: '#0d1520',
    border: '1px solid #1e3a5f',
    borderRadius: 12,
    width: '100%', maxWidth: 640,
    maxHeight: '90vh',
    display: 'flex', flexDirection: 'column',
    boxShadow: '0 24px 64px rgba(0,0,0,0.7)',
    overflow: 'hidden',
  },
  header: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
    padding: '18px 24px 14px',
    borderBottom: '1px solid #1e3a5f',
    background: 'linear-gradient(135deg, #0d1520 0%, #0f2040 100%)',
    flexShrink: 0,
  },
  headerTitle: { fontSize: 17, fontWeight: 700, color: '#38bdf8' },
  headerSub: { fontSize: 12, color: '#64748b', marginTop: 3 },
  closeBtn: { background: 'none', border: 'none', color: '#64748b', fontSize: 20, cursor: 'pointer' },
  filtriBar: {
    display: 'flex', gap: 8, padding: '10px 14px',
    borderBottom: '1px solid #1e3a5f', flexShrink: 0,
  },
  searchInput: {
    flex: 1, padding: '11px 14px',
    background: '#0f1623', border: '1px solid #1e3a5f',
    borderRadius: 8, color: '#e2e8f0', fontSize: 15, outline: 'none',
    minHeight: 46,
  },
  selectRegione: {
    padding: '11px 12px',
    background: '#0f1623', border: '1px solid #1e3a5f',
    borderRadius: 8, color: '#e2e8f0', fontSize: 14,
    minHeight: 46,
  },
  allegatoNotice: {
    display: 'flex', alignItems: 'flex-start', gap: 10,
    margin: '0 12px 0',
    padding: '10px 14px',
    background: '#1c1000',
    border: '1px solid #92400e',
    borderRadius: 8,
    color: '#fbbf24',
    fontSize: 13,
    flexShrink: 0,
  },
  lista: {
    overflowY: 'auto', flex: 1,
    padding: '8px 12px',
    display: 'flex', flexDirection: 'column', gap: 4,
  },
  vuoto: { color: '#475569', fontSize: 13, textAlign: 'center', padding: 24 },
  riga: {
    display: 'flex', alignItems: 'center', gap: 12,
    padding: '14px 14px', borderRadius: 10,
    cursor: 'pointer', transition: 'all 0.15s',
    minHeight: 64,
  },
  rigaCheck: { flexShrink: 0 },
  checkbox: {
    width: 30, height: 30, borderRadius: 6,
    border: '2px solid', display: 'flex',
    alignItems: 'center', justifyContent: 'center',
    flexShrink: 0,
  },
  rigaInfo: { flex: 1, minWidth: 0 },
  rigaNome: {
    fontSize: 14, fontWeight: 600, color: '#e2e8f0',
    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
  },
  rigaEmail: {
    fontSize: 12, color: '#64748b', marginTop: 3,
    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
  },
  rigaNote: { fontSize: 11, color: '#475569', marginTop: 2 },
  rigaSigla: {
    fontSize: 12, fontWeight: 700, color: '#38bdf8',
    background: '#0f2a3a', padding: '4px 9px', borderRadius: 5,
    flexShrink: 0,
  },
  rigaAzioni: { display: 'flex', gap: 6, flexShrink: 0 },
  badgeSpeciale: {
    display: 'inline-block', marginRight: 5,
    color: '#f97316', fontSize: 11,
  },
  badgeCustom: {
    display: 'inline-block', marginRight: 5,
    color: '#a78bfa', fontSize: 11,
  },
  btnEdit: {
    background: '#1e3a5f', border: 'none', color: '#94a3b8',
    borderRadius: 6, padding: '7px 12px', cursor: 'pointer', fontSize: 14, minWidth: 40, minHeight: 40,
  },
  btnDel: {
    background: '#3f0f0f', border: 'none', color: '#f87171',
    borderRadius: 6, padding: '7px 12px', cursor: 'pointer', fontSize: 14, minWidth: 40, minHeight: 40,
  },
  footer: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '14px 16px', borderTop: '1px solid #1e3a5f',
    flexShrink: 0, background: '#0d1520',
  },
  btnAggiungi: {
    padding: '11px 16px', borderRadius: 8,
    border: '1px solid #a78bfa', background: 'transparent',
    color: '#a78bfa', fontSize: 14, cursor: 'pointer', fontWeight: 600, minHeight: 46,
  },
  selezionatiLabel: {
    fontSize: 13, color: '#38bdf8',
    background: '#0f2a3a', padding: '6px 12px', borderRadius: 6,
  },
  btnAnnulla: {
    padding: '11px 18px', borderRadius: 8,
    border: '1px solid #1e3a5f', background: 'transparent',
    color: '#64748b', fontSize: 14, cursor: 'pointer', minHeight: 46,
  },
  btnInvia: {
    padding: '12px 24px', borderRadius: 8, border: 'none',
    background: 'linear-gradient(135deg, #0ea5e9, #0284c7)',
    color: '#fff', fontSize: 15, fontWeight: 700, cursor: 'pointer',
    boxShadow: '0 4px 12px rgba(14,165,233,0.35)', minHeight: 46,
  },
  // Form aggiungi/modifica
  formBody: {
    overflowY: 'auto', flex: 1, padding: '16px 20px',
    display: 'flex', flexDirection: 'column', gap: 14,
  },
  formTitle: {
    fontSize: 15, fontWeight: 700, color: '#a78bfa', marginBottom: 4,
  },
  formGrp: { display: 'flex', flexDirection: 'column', gap: 6 },
  formLabel: { fontSize: 13, color: '#94a3b8', fontWeight: 600 },
  formInput: {
    padding: '12px 14px',
    background: '#0f1623', border: '1px solid #1e3a5f',
    borderRadius: 8, color: '#e2e8f0', fontSize: 15, outline: 'none',
    minHeight: 48,
  },
  erroreForm: {
    fontSize: 12, color: '#f87171',
    background: '#1a0a0a', border: '1px solid #7f1d1d',
    borderRadius: 4, padding: '6px 10px',
  },
};

export default ModalInvioEmail;
